import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api';
import { localDate, shortDate } from '../format';
import type { Dashboard, Person } from '../types';

export function InspectionsPanel({ dashboard, onReload }: { dashboard: Dashboard; onReload: () => Promise<void> }) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [createdLink, setCreatedLink] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [form, setForm] = useState({
    roomId: dashboard.rooms[0]?.id ?? '',
    scope: 'FULL' as 'FULL' | 'COMMON_ONLY' | 'PRIVATE_ONLY',
    type: 'EXIT' as 'ENTRY' | 'EXIT',
    tenantName: '',
    scheduledFor: localDate(),
    expiresInHours: 336,
  });

  // Suggestions plutôt qu'une liste fermée : un état des lieux peut concerner
  // quelqu'un pas encore fiché (visite d'un prospect, cas rare).
  useEffect(() => {
    api<{ people: Person[] }>('/api/admin/people').then((result) => setPeople(result.people)).catch(() => undefined);
  }, []);
  const tenantSuggestions = [...people]
    .sort((left, right) => (left.category === 'TENANT' ? -1 : 0) - (right.category === 'TENANT' ? -1 : 0));

  async function createMission(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');
    setCreatedLink('');
    try {
      const result = await api<{ link: string }>('/api/admin/inspections', {
        method: 'POST',
        // Un constat des parties communes ne porte aucune chambre : le serveur
        // refuse la combinaison, autant ne pas l'envoyer.
        body: JSON.stringify({ ...form, roomId: form.scope === 'COMMON_ONLY' ? null : form.roomId }),
      });
      setCreatedLink(result.link);
      setForm((current) => ({ ...current, tenantName: '' }));
      await onReload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Création impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(grantId: string): Promise<void> {
    if (!window.confirm('Révoquer immédiatement ce lien et les sessions associées ?')) return;
    // Sans capture, un échec de révocation partait en rejet non traité : la
    // croix restait affichée, l'écran semblait figé et l'erreur n'était nulle
    // part. Constaté en recette le 17/08/2026.
    try {
      await api(`/api/admin/grants/${grantId}/revoke`, { method: 'POST' });
      await onReload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Révocation impossible.');
    }
  }

  async function copyLink(): Promise<void> {
    await navigator.clipboard.writeText(createdLink);
  }

  async function removeDraft(inspectionId: string): Promise<void> {
    if (!window.confirm('Retirer ce brouillon d’état des lieux ? Il disparaît des échéances à venir.')) return;
    try {
      await api(`/api/admin/inspections/${inspectionId}/cancel`, { method: 'POST' });
      await onReload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Suppression impossible.');
    }
  }

  return (
    <div className="admin-grid">
      <section className="mission-maker panel">
        <p className="eyebrow">Accès ponctuel</p>
        <h2>Confier un état des lieux</h2>
        <p className="section-intro">Le lien ouvre uniquement cette mission. Il expire automatiquement et reste révocable.</p>
        <form onSubmit={(event) => void createMission(event)} className="form-grid">
          <fieldset className="segmented-field span-2">
            <legend>Portée du constat</legend>
            <div className="segmented">
              <button type="button" className={form.scope === 'FULL' ? 'active' : ''} onClick={() => setForm({ ...form, scope: 'FULL' })}>Chambre et communs</button>
              <button type="button" className={form.scope === 'PRIVATE_ONLY' ? 'active' : ''} onClick={() => setForm({ ...form, scope: 'PRIVATE_ONLY' })}>Chambre seule</button>
              <button type="button" className={form.scope === 'COMMON_ONLY' ? 'active' : ''} onClick={() => setForm({ ...form, scope: 'COMMON_ONLY' })}>Parties communes</button>
            </div>
            <p className="muted">
              {form.scope === 'COMMON_ONLY'
                ? "Les parties communes appartiennent au logement : ce constat ne vise aucune chambre et son résultat vaut pour les quatre locataires."
                : form.scope === 'PRIVATE_ONLY'
                  ? 'Seuls les éléments de la chambre sont parcourus. Utile quand les communs viennent d’être constatés.'
                  : 'Les 91 éléments, chambre et parties communes.'}
            </p>
          </fieldset>
          {form.scope !== 'COMMON_ONLY' && (
            <label>Chambre
              <select value={form.roomId} onChange={(event) => setForm({ ...form, roomId: event.target.value })} required>
                {dashboard.rooms.map((room) => <option key={room.id} value={room.id}>{room.name}{room.hasBalcony ? ' · balcon' : ''}</option>)}
              </select>
            </label>
          )}
          <fieldset className="segmented-field">
            <legend>Type</legend>
            <div className="segmented">
              <button type="button" className={form.type === 'ENTRY' ? 'active' : ''} onClick={() => setForm({ ...form, type: 'ENTRY' })}>Entrée</button>
              <button type="button" className={form.type === 'EXIT' ? 'active' : ''} onClick={() => setForm({ ...form, type: 'EXIT' })}>Sortie</button>
            </div>
          </fieldset>
          <label className="span-2">Nom du locataire
            <input
              value={form.tenantName}
              onChange={(event) => setForm({ ...form, tenantName: event.target.value })}
              required
              minLength={2}
              placeholder="Prénom et nom"
              list="tenant-suggestions"
            />
            <datalist id="tenant-suggestions">
              {tenantSuggestions.map((person) => <option key={person.id} value={`${person.firstName} ${person.lastName}`} />)}
            </datalist>
          </label>
          <label>Date prévue
            <input type="date" value={form.scheduledFor} onChange={(event) => setForm({ ...form, scheduledFor: event.target.value })} required />
          </label>
          <label>Lien valable
            <select value={form.expiresInHours} onChange={(event) => setForm({ ...form, expiresInHours: Number(event.target.value) })}>
              <option value={72}>3 jours</option><option value={168}>7 jours</option><option value={336}>14 jours</option><option value={720}>30 jours</option>
            </select>
          </label>
          <button className="button primary span-2" disabled={busy}>{busy ? 'Création…' : 'Créer le lien de mission'}</button>
        </form>
        {createdLink && (
          <div className="link-result" role="status">
            <div>
              <strong>Lien prêt</strong>
              <span>Envoyez-le uniquement à la personne concernée. Copiez-le maintenant : il ne sera plus
              affichable, le serveur n'en garde qu'une empreinte.</span>
            </div>
            <button className="button compact" onClick={() => void copyLink()}>Copier</button>
            <code>{createdLink}</code>
          </div>
        )}
        {error && <div className="inline-message error" role="alert">{error}</div>}
      </section>
      <section className="panel mission-list">
        <div className="section-heading"><div><p className="eyebrow">Suivi</p><h2>Missions récentes</h2></div><span className="count-badge">{dashboard.inspections.length}</span></div>
        {dashboard.inspections.length === 0 && <div className="empty-state">Aucune mission. Créez le prochain état des lieux à gauche.</div>}
        {dashboard.inspections.map((inspection) => (
          <article className="mission-row" key={inspection.id}>
            <div className={`mission-mark ${inspection.type.toLowerCase()}`}>{inspection.type === 'ENTRY' ? 'E' : 'S'}</div>
            <div className="mission-main">
              <strong>{inspection.roomName} · {inspection.tenantName}</strong>
              <span>{inspection.type === 'ENTRY' ? 'Entrée' : 'Sortie'} le {shortDate(inspection.scheduledFor)}</span>
            </div>
            <span className={`status ${inspection.status.toLowerCase()}`}>{inspection.status === 'SIGNED' ? 'Terminé' : inspection.status === 'DRAFT' ? 'À faire' : inspection.status}</span>
            <div className="mission-actions">
              {inspection.status === 'DRAFT' && (
                <button className="icon-button danger" title="Retirer ce brouillon" aria-label="Retirer ce brouillon" onClick={() => void removeDraft(inspection.id)}>🗑</button>
              )}
              {inspection.grant && !inspection.grant.revoked && inspection.status !== 'SIGNED' && (
                <button className="icon-button danger" title="Révoquer le lien" aria-label="Révoquer le lien" onClick={() => void revoke(inspection.grant!.id)}>×</button>
              )}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
